import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { BehaviorSubject, Observable, take } from 'rxjs';

import {
  AlarmDto,
  AlarmFindAndCountResponseDto,
  AlarmListQueryDto,
  AlarmResponseDto,
  AlarmSeverityCountDto,
  AlarmSeverityCountResponseDto,
} from '../types/dtos/alarm.dto';
import { DeviceDto } from '../types/dtos/device.dto';
import { WebsocketResponseDto } from '../types/dtos/websocket.dto';
import { AlarmSeverity, AlarmStatus, AlarmType } from '../types/enums/alarm.enum';
import { DomainEnum } from '../types/enums/websocket.enum';
import { AlarmStatusUpdateDataInterface } from '../types/interfaces/alarm.interfaces';
import { TableTagSeverity } from '../types/interfaces/table.interface';
import { AuthService } from './auth.service';
import { BaseService } from './base.service';
import { WebsocketService } from './websocket.service';

@Injectable({
  providedIn: 'root',
})
export class AlarmService extends BaseService {
  private readonly _url: string = `${ this._baseUrl }/alarm`;
  private _alarmTypeFilter: BehaviorSubject<AlarmType[]> = new BehaviorSubject<AlarmType[]>([]);

  public alarmTypeFilter$: Observable<AlarmType[]> = this._alarmTypeFilter.asObservable();

  private readonly STATUS_LABEL_MAP: Record<AlarmStatus, string> = {
    [AlarmStatus.ACTIVE]: 'Aktiv',
    [AlarmStatus.ACKNOWLEDGED]: 'Bestätigt',
    [AlarmStatus.RESOLVED]: 'Gelöst',
  };

  private readonly STATUS_COLOR_MAP: Record<AlarmStatus, TableTagSeverity> = {
    [AlarmStatus.ACTIVE]: TableTagSeverity.DANGER,
    [AlarmStatus.ACKNOWLEDGED]: TableTagSeverity.WARN,
    [AlarmStatus.RESOLVED]: TableTagSeverity.INFO,
  };

  private readonly SEVERITY_LABEL_MAP: Record<AlarmSeverity, string> = {
    [AlarmSeverity.CRITICAL]: 'Kritisch',
    [AlarmSeverity.HIGH]: 'Hoch',
    [AlarmSeverity.WARNING]: 'Warnung',
    [AlarmSeverity.INFO]: 'Info',
  };

  private readonly SEVERITY_COLOR_MAP: Record<AlarmSeverity, TableTagSeverity> = {
    [AlarmSeverity.CRITICAL]: TableTagSeverity.DANGER,
    [AlarmSeverity.HIGH]: TableTagSeverity.DANGER, // TODO Color Must be Changed
    [AlarmSeverity.WARNING]: TableTagSeverity.WARN,
    [AlarmSeverity.INFO]: TableTagSeverity.INFO,
  };

  private alarmSeverityCountSubject: BehaviorSubject<AlarmSeverityCountDto> =
    new BehaviorSubject<AlarmSeverityCountDto>({
      [AlarmSeverity.INFO]: 0,
      [AlarmSeverity.WARNING]: 0,
      [AlarmSeverity.HIGH]: 0,
      [AlarmSeverity.CRITICAL]: 0,
    });

  public alarmSeverityCount$: Observable<AlarmSeverityCountDto> = this.alarmSeverityCountSubject.asObservable();

  private unResolvedAlarmsSubject: BehaviorSubject<AlarmDto[]> =
    new BehaviorSubject<AlarmDto[]>([]);
  public unResolvedAlarms$: Observable<AlarmDto[]> = this.unResolvedAlarmsSubject.asObservable();

  private readonly updatedAlarmSubject: BehaviorSubject<AlarmDto | null> = new BehaviorSubject<AlarmDto | null>(null);
  public readonly updatedAlarm$: Observable<AlarmDto | null> = this.updatedAlarmSubject.asObservable();

  constructor(
    private readonly _httpClient: HttpClient,
    private readonly _websocketService: WebsocketService,
    private readonly _authService: AuthService,
    private readonly _translateService: TranslateService,
  ) {
    super();
    this._init()
      .then();
  }

  public getAll(query: AlarmListQueryDto = {}): Observable<AlarmFindAndCountResponseDto> {
    return this._httpClient
      .get<AlarmFindAndCountResponseDto>(`${ this._url }/`, {
        params: this.createQueryParams(query),
      })
      .pipe(take(1));
  }

  public getById(id: number): Observable<AlarmResponseDto> {
    return this._httpClient
      .get<AlarmResponseDto>(`${ this._url }/${ id }`)
      .pipe(take(1));
  }

  public toggleAlarmTypeFilter(alarmType: AlarmType, isHidden: boolean): void {
    const currentAlarmTypeFilter: AlarmType[] = this._alarmTypeFilter.value;
    const doesExist: boolean = currentAlarmTypeFilter.includes(alarmType);

    if (isHidden && doesExist) {
      const index: number = currentAlarmTypeFilter.indexOf(alarmType);

      currentAlarmTypeFilter.splice(index, 1);
    }

    if (!isHidden && !doesExist) {
      currentAlarmTypeFilter.push(alarmType);
    }

    this._alarmTypeFilter.next(currentAlarmTypeFilter);
  }

  /**
   * Fetches and filters unresolved alarms based on their status being either ACTIVE or ACKNOWLEDGED.
   * The resulting list excludes alarms with the status RESOLVED and updates the observable subject
   * `unResolvedAlarmsSubject` with the filtered alarms.
   *
   * @return {void} This method does not return a value directly but updates the internal observable subject for
   *   unresolved alarms.
   */
  public fetchUnResolvedAlarms(): void {
    this.getAll({
      status: [
        AlarmStatus.ACTIVE,
        AlarmStatus.ACKNOWLEDGED,
      ],
      limit: -1,
    })
      .subscribe((alarms: AlarmFindAndCountResponseDto) => {
        this.unResolvedAlarmsSubject.next(alarms.data.filter((alarm: AlarmDto) => alarm.status !== AlarmStatus.RESOLVED));
      });
  }

  public getUnResolvedAlarms(): AlarmDto[] {
    return this.unResolvedAlarmsSubject.value;
  }

  public setAlarmStatus(): void {
    this._httpClient
      .get<AlarmSeverityCountResponseDto>(`${ this._url }/status`)
      .pipe(take(1))
      .subscribe((alarmSeverityCount: AlarmSeverityCountResponseDto) => {
        this.alarmSeverityCountSubject.next(alarmSeverityCount.data);
      });
  }

  public acknowledged(id: number, updateData: AlarmStatusUpdateDataInterface): Observable<AlarmResponseDto> {
    const formData: FormData = new FormData();
    if (updateData.imageFile) {
      formData.append('file', updateData.imageFile);
    }
    formData.append('comment', updateData.comment);

    return this._httpClient
      .post<AlarmResponseDto>(
        `${ this._url }/${ id }/acknowledged`,
        formData,
      )
      .pipe(take(1));
  }

  public resolved(id: number, updateData: AlarmStatusUpdateDataInterface): Observable<AlarmResponseDto> {
    const formData: FormData = new FormData();
    if (updateData.imageFile) {
      formData.append('file', updateData.imageFile);
    }
    formData.append('comment', updateData.comment);

    return this._httpClient
      .post<AlarmResponseDto>(
        `${ this._url }/${ id }/resolved`,
        formData,
      )
      .pipe(take(1));
  }

  public getActiveAlarms(device: DeviceDto): AlarmDto[] {
    if (!device.alarms) return [];
    return device.alarms.filter((alarm: AlarmDto) => alarm.status !== AlarmStatus.RESOLVED);
  }

  public mapAlarmStatusClass(status: AlarmStatus): TableTagSeverity {
    return this.STATUS_COLOR_MAP[status];
  }

  public mapAlarmStatusLabel(status: AlarmStatus): string {
    return this.STATUS_LABEL_MAP[status];
  }

  public mapAlarmSeverityLabel(severity: AlarmSeverity): string {
    return this.SEVERITY_LABEL_MAP[severity];
  }

  public mapAlarmSeverityClass(severity: AlarmSeverity): TableTagSeverity {
    return this.SEVERITY_COLOR_MAP[severity];
  }

  private async _init() {
    await this._authService.waitUntilLogin();
    this.setAlarmStatus();
    this.fetchUnResolvedAlarms();
    this.listenToUpdates();
  }

  private listenToUpdates(): void {
    this._websocketService.subscribe(
      DomainEnum.ALARM,
      'update',
      (payload: WebsocketResponseDto) => {
        if (payload.params && payload.params['id']) {
          this._updateAlarm(Number(payload.params['id']));
          return;
        }

        this.fetchUnResolvedAlarms();
      },
    );
  }

  private _updateAlarm(id: number): void {
    this.getById(id)
      .subscribe({
        next: (result: AlarmResponseDto) => {
          if (!result.data) return;

          this._updateDeviceInSubject(id, result.data);
          this.updatedAlarmSubject.next(result.data);
        },
        error: (error: unknown) => {
          console.error('Error fetching device:', error);
        },
      });
  }

  private _updateDeviceInSubject(id: number, device: AlarmDto): void {
    const currentDevices: AlarmDto[] = [...this.unResolvedAlarmsSubject.value];
    const deviceIndex: number = currentDevices.findIndex(
      (currentDevice: AlarmDto) => currentDevice.id === id,
    );

    if (deviceIndex !== -1) {
      currentDevices[deviceIndex] = device;
    } else {
      currentDevices.push(device);
    }

    this.unResolvedAlarmsSubject.next(currentDevices);
  }
}
